import { useParams, Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { Helmet } from "react-helmet-async";
import { ArrowRight, ExternalLink, Newspaper, Play, Building2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { useBrand } from "@/contexts/brand-context";
import { useEffect } from "react";
import { getArticleUrl } from "@/lib/article-url";
import type { Article, Company } from "@shared/schema";
import { useSiteContent } from "@/hooks/use-site-content";

interface BrandData {
  name: string;
  strapline: string;
  color: string;
  description: string;
  longDescription: string;
  website?: string;
  subBrands?: { name: string; slug: string; desc: string; color: string }[];
}

const BRAND_DATA: Record<string, BrandData> = {
  "future-manufacturing": {
    name: "Future Manufacturing",
    strapline: "The whole BoM: Mechanical, Electrical, Electronic",
    color: "from-slate-800 to-slate-600",
    description:
      "Future Manufacturing is the parent brand and digital home for a family of specialist media titles covering the complete bill of materials — from mechanical components and electronic assemblies to software-defined systems and sustainable engineering.",
    longDescription:
      "We believe great engineering happens at the intersections. A mechanical engineer who understands PCB layout. An electronics designer who cares about embodied carbon. A software architect who knows their way around a tolerance stack. Future Manufacturing connects those dots — bringing together the sub-brands, communities, and intelligence that serve the whole BoM.",
    subBrands: [
      { name: "Sustainable Engineering Alliance", slug: "sustainable-engineering-alliance", desc: "Sustainability, lifecycle analysis & circular design", color: "bg-emerald-500" },
      { name: "Disti", slug: "disti", desc: "Electronics, electrical & PCB design", color: "bg-blue-500" },
      { name: "Devkit", slug: "devkit", desc: "Development kits, prototyping & embedded systems", color: "bg-violet-500" },
    ],
  },
  "sustainable-engineering-alliance": {
    name: "Sustainable Engineering Alliance",
    strapline: "Engineering a circular future",
    color: "from-emerald-700 to-teal-600",
    description: "The Sustainable Engineering Alliance is the knowledge hub for engineers and product teams committed to reducing the environmental impact of the things they make — from design for disassembly to whole-lifecycle carbon accounting.",
    longDescription: "Sustainability in engineering is no longer a nice-to-have. Regulation, procurement requirements, and customer expectations are converging. The Sustainable Engineering Alliance brings together the tools, case studies, data, and community that help teams embed sustainability into every stage of the product lifecycle — from material selection and DfX to end-of-life recovery and reporting.",
    website: "https://sustainableengineeringalliance.com",
  },
  "disti": {
    name: "Disti",
    strapline: "Electronics, electrical & PCB design",
    color: "from-blue-700 to-indigo-600",
    description: "Disti is the intelligence platform for electronics engineers, covering component selection, PCB design, power electronics, EMC, and the fast-moving world of electronic distribution.",
    longDescription: "From silicon to system, Disti tracks what matters to electronics engineers. Component availability, design tips, technical deep-dives on new silicon, and the business intelligence that helps procurement and engineering teams navigate the electronics supply chain.",
    website: "https://disti.com",
  },
  "devkit": {
    name: "Devkit",
    strapline: "Development kits, prototyping & embedded systems",
    color: "from-violet-700 to-purple-600",
    description: "Devkit is the go-to resource for embedded systems engineers and hardware prototypers — covering development boards, RTOS, firmware toolchains, and the maker-to-manufacturer journey.",
    longDescription: "The gap between a working prototype and a production-ready product is where most hardware projects stall. Devkit helps engineers cross that gap — with reviews of development kits and evaluation boards, guidance on RTOS selection and firmware architecture, and practical advice on taking a prototype through DFM and into volume production.",
  },
};

const SUB_BRAND_SLUGS = new Set(["sustainable-engineering-alliance", "disti", "devkit"]);

interface ArticleWithHero extends Article {
  heroImageUrl?: string | null;
}

function ArticleMiniCard({ article }: { article: ArticleWithHero }) {
  const imageUrl = article.videoThumbnail || article.heroImageUrl || article.featuredImage;
  const isVideo = article.contentType === "video" || article.contentType === "mixed";
  return (
    <Link href={getArticleUrl(article)} data-testid={`card-brand-article-${article.id}`}>
      <div className="group flex gap-3 p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors cursor-pointer">
        <div className="w-16 h-12 rounded overflow-hidden bg-muted flex-shrink-0 relative">
          {imageUrl ? (
            <img src={imageUrl} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              {isVideo ? <Play className="h-5 w-5 text-muted-foreground" /> : <Newspaper className="h-5 w-5 text-muted-foreground" />}
            </div>
          )}
          {isVideo && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
              <Play className="h-4 w-4 text-white" fill="white" />
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium leading-snug line-clamp-2 group-hover:text-primary transition-colors">{article.title}</p>
          {article.publishedAt && (
            <p className="text-xs text-muted-foreground mt-1">
              {new Date(article.publishedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
            </p>
          )}
        </div>
      </div>
    </Link>
  );
}

function CompanyMiniCard({ company }: { company: Company }) {
  return (
    <Link href={`/companies/${company.slug}`} data-testid={`card-brand-company-${company.id}`}>
      <div className="group flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors cursor-pointer">
        <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
          {company.logoUrl ? (
            <img src={company.logoUrl} alt={company.name} className="w-8 h-8 object-contain" />
          ) : (
            <Building2 className="h-5 w-5 text-muted-foreground" />
          )}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium leading-tight line-clamp-1 group-hover:text-primary transition-colors">{company.name}</p>
          {company.industry && <p className="text-xs text-muted-foreground">{company.industry}</p>}
        </div>
      </div>
    </Link>
  );
}

function SubBrandLiveFeed({ slug }: { slug: string }) {
  const { data: articles, isLoading: articlesLoading } = useQuery<ArticleWithHero[]>({
    queryKey: ["/api/public/brands", slug, "articles"],
    queryFn: async () => {
      const res = await fetch(`/api/public/brands/${slug}/articles`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
  });

  const { data: stats } = useQuery<{ articleCount: number; videoCount: number; companyCount: number }>({
    queryKey: ["/api/public/brands", slug, "stats"],
    queryFn: async () => {
      const res = await fetch(`/api/public/brands/${slug}/stats`, { credentials: "include" });
      if (!res.ok) return { articleCount: 0, videoCount: 0, companyCount: 0 };
      return res.json();
    },
  });

  const { data: companies } = useQuery<Company[]>({
    queryKey: ["/api/public/brands", slug, "companies"],
    queryFn: async () => {
      const res = await fetch(`/api/public/brands/${slug}/companies`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
  });

  const latestArticles = (articles ?? []).slice(0, 6);
  const latestCompanies = (companies ?? []).slice(0, 6);

  return (
    <div className="mt-12 space-y-10">
      {/* Stats */}
      {stats && (stats.articleCount > 0 || stats.videoCount > 0 || stats.companyCount > 0) && (
        <div className="flex gap-6 flex-wrap">
          {stats.articleCount > 0 && (
            <div className="text-center">
              <p className="text-3xl font-bold">{stats.articleCount}</p>
              <p className="text-sm text-muted-foreground">Articles</p>
            </div>
          )}
          {stats.videoCount > 0 && (
            <div className="text-center">
              <p className="text-3xl font-bold">{stats.videoCount}</p>
              <p className="text-sm text-muted-foreground">Videos</p>
            </div>
          )}
          {stats.companyCount > 0 && (
            <div className="text-center">
              <p className="text-3xl font-bold">{stats.companyCount}</p>
              <p className="text-sm text-muted-foreground">Member Companies</p>
            </div>
          )}
        </div>
      )}

      {/* Latest Articles */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">Latest Articles</h2>
          <Link href={`/articles?brand=${slug}`}>
            <span className="text-sm text-primary hover:underline flex items-center gap-1 cursor-pointer">
              View all <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </Link>
        </div>
        {articlesLoading ? (
          <div className="grid sm:grid-cols-2 gap-3">
            {[1,2,3,4].map(i => <Skeleton key={i} className="h-20 rounded-lg" />)}
          </div>
        ) : latestArticles.length > 0 ? (
          <div className="grid sm:grid-cols-2 gap-3">
            {latestArticles.map(a => <ArticleMiniCard key={a.id} article={a} />)}
          </div>
        ) : (
          <div className="flex items-center gap-3 p-6 rounded-lg border bg-muted/30 text-muted-foreground">
            <Newspaper className="h-5 w-5" />
            <p className="text-sm">No articles tagged to this brand yet.</p>
          </div>
        )}
      </div>

      {/* Featured Companies */}
      {latestCompanies.length > 0 && (
        <div>
          <h2 className="text-xl font-bold mb-4">Featured Companies</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {latestCompanies.map(c => <CompanyMiniCard key={c.id} company={c} />)}
          </div>
        </div>
      )}
    </div>
  );
}

const SLUG_TO_PREFIX: Record<string, string> = {
  "future-manufacturing": "fm",
  "sustainable-engineering-alliance": "sea",
  "disti": "disti",
  "devkit": "devkit",
};

export default function BrandPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const brand = BRAND_DATA[slug];
  const { setBrand } = useBrand();
  const { getJsonContent } = useSiteContent("brand");

  type SC = { visible?: boolean; heading?: string; subtitle?: string; fieldVisible?: Record<string, boolean>; [key: string]: unknown };
  const fv = (cfg: SC, key: string) => cfg.fieldVisible?.[key] !== false;
  const secBrandHero   = getJsonContent<SC>("section_hero", {});
  const secSubBrands   = getJsonContent<SC>("section_sub_brands", {});
  const prefix = SLUG_TO_PREFIX[slug] ?? slug.replace(/-/g, "_");

  useEffect(() => {
    if (SUB_BRAND_SLUGS.has(slug)) setBrand(slug);
  }, [slug]);

  if (!brand) {
    return (
      <>
        <SiteHeader />
        <main className="mx-auto max-w-7xl px-4 py-20 text-center">
          <h1 className="text-2xl font-bold mb-4">Brand not found</h1>
          <p className="text-muted-foreground mb-6">We couldn't find a brand at this address.</p>
          <Link href="/brand/future-manufacturing">
            <span className="text-primary hover:underline cursor-pointer">View all brands →</span>
          </Link>
        </main>
      </>
    );
  }

  const isSubBrand = SUB_BRAND_SLUGS.has(slug);

  return (
    <>
      <Helmet>
        <title>{brand.name} — Future Manufacturing</title>
        <meta name="description" content={brand.description} />
        <meta property="og:title" content={`${brand.name} — Future Manufacturing`} />
        <meta property="og:description" content={brand.description} />
        <meta property="og:site_name" content={brand.name} />
      </Helmet>

      <SiteHeader />

      {/* Hero */}
      <div className={`bg-gradient-to-br ${brand.color} text-white`}>
        <div className="mx-auto max-w-7xl px-4 py-16">
          <p className="text-sm font-semibold uppercase tracking-widest opacity-70 mb-3">A Future Manufacturing brand</p>
          {fv(secBrandHero, `${prefix}_heading`) && (
            <h1 className="text-4xl font-bold tracking-tight mb-3">{brand.name}</h1>
          )}
          {fv(secBrandHero, `${prefix}_description`) && (
            <p className="text-xl opacity-80 italic">{brand.strapline}</p>
          )}
          {brand.website && (
            <a href={brand.website} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 mt-6 text-sm font-medium bg-white/10 hover:bg-white/20 transition-colors rounded-md px-4 py-2"
              data-testid="link-brand-website"
            >
              Visit website <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
      </div>

      {/* Body */}
      <main className="mx-auto max-w-7xl px-4 py-12">
        <div className="max-w-3xl">
          <p className="text-lg text-foreground leading-relaxed mb-4">{brand.description}</p>
          <p className="text-base text-muted-foreground leading-relaxed">{brand.longDescription}</p>
        </div>

        {/* Sub-brand listing (FM parent page only) */}
        {brand.subBrands && brand.subBrands.length > 0 && (
          <div className="mt-16">
            {fv(secSubBrands, 'heading') && <h2 className="text-xl font-bold mb-2">Our Brands</h2>}
            {fv(secSubBrands, 'subtitle') && <p className="text-muted-foreground mb-8">Specialist titles across the whole bill of materials.</p>}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {brand.subBrands.map(sub => (
                <Link key={sub.slug} href={`/brand/${sub.slug}`} data-testid={`card-brand-${sub.slug}`}>
                  <div className="group border border-border rounded-xl p-6 hover:shadow-md transition-shadow cursor-pointer bg-card">
                    <div className="flex items-center gap-3 mb-3">
                      <span className={`w-3 h-3 rounded-full flex-shrink-0 ${sub.color}`} />
                      <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">{sub.name}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">{sub.desc}</p>
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-primary group-hover:gap-2 transition-all">
                      Explore <ArrowRight className="h-3 w-3" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Live content feed (sub-brand pages) */}
        {isSubBrand && <SubBrandLiveFeed slug={slug} />}

        {/* Back link on sub-brand pages */}
        {!brand.subBrands && (
          <div className="mt-12 pt-8 border-t border-border">
            <Link href="/brand/future-manufacturing" data-testid="link-back-to-fm">
              <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
                ← Part of Future Manufacturing
              </span>
            </Link>
          </div>
        )}
      </main>

      <SiteFooter />
    </>
  );
}
